import { useMemo } from 'react';
import {
  useDoctorPresence,
  type DoctorPresence,
} from '@/scheduling/services/use-doctor-presence';
import { useResourceAvailability } from '@/scheduling/services/use-device-availability';
import { parseAvailability } from '@/scheduling/lib/device-availability';
import {
  BLOCK_ACTOR_DEVICE,
  type BlockActorKind,
} from '@/types/block-availability-api';
import type { ResourceAvailabilityPeriod } from '@/types/scheduling-api';

/**
 * The break form's SHIFT list, for either kind of actor (PROD-2454).
 *
 * The break form was never practitioner-specific in its logic — it bounds the
 * start/end hour dropdowns to a working window and nothing more — but it WAS
 * practitioner-specific in where that window came from: it called
 * `GET /api/Doctor/Presence` directly. A machine has no doctor presence. This
 * hook is the seam that removes the assumption without changing the form:
 * one shape in, two feeds behind it.
 *
 *   practitioner → `GET /api/Doctor/Presence`      (the shifts as before)
 *   device       → `GET /api/Slots/ResourceAvailability` (PROD-2007)
 *
 * The two responses are near-identical objects — `start`/`end`/`duration`/
 * `isMoment`/`isAnytime`/`hasStart`/`hasEnd`/`isReadOnly` — because they come
 * from the same PracticeManagement source (`GetActorPresence`, per actor
 * type). So the device side is projected onto `DoctorPresence` rather than
 * the form learning a second shape; see `deviceShiftsFromAvailability` for
 * the two things that projection has to get right.
 *
 * BOTH queries are declared unconditionally and gated by `enabled`, so the
 * hook order is stable when the panel switches actor kind — the one rule a
 * dispatching hook can break (see memory
 * `feedback_verify_ui_across_the_loading_transition.md`).
 *
 * KNOWN DIVERGENCE, deliberate: a practitioner's presence is their raw
 * working hours, while `ResourceAvailability` is the device's working hours
 * MINUS every slot already on its schedule. So the device shift list offers
 * only the windows the machine is actually free in, and an operator cannot
 * start a device block on top of an existing booking from the picker. That is
 * narrower than the practitioner flow, and it is the safe direction: it can
 * only refuse a block the server would have answered with `blockers` anyway.
 * The panel still handles `blockers` identically for both kinds, because the
 * race is real — a booking made after this list was fetched lands inside a
 * window the picker is still offering.
 */

export interface UseActorShiftsParams {
  kind: BlockActorKind;
  /** Practitioner or device GUID. Null until an actor is picked. */
  actorId: string | null;
  /** Practice GUID — required by the device feed, ignored by the doctor one. */
  practiceId: string;
  /** The day being blocked. */
  date: Date | null;
}

export interface UseActorShiftsResult {
  shifts: DoctorPresence[];
  isLoading: boolean;
}

/**
 * `YYYY-MM-DDTHH:mm:00Z` built from a LOCAL Date's local components.
 *
 * Not a mistake, and the single most breakable line in this file. The break
 * form reads a shift's hour with `new Date(iso).getUTCHours()`, because
 * `/api/Doctor/Presence` ships clinic wall clock stamped `Z`
 * (`use-doctor-presence.ts` says so at `compactTime` / `presenceTimeOfDay`).
 * `ResourceAvailability` ships the same wall clock but is parsed TEXTUALLY
 * into a local Date (`device-availability.ts`). To hand the form a shift it
 * reads correctly, the wall clock has to be re-stamped as UTC. Emitting a
 * plain local ISO here would shift every device shift by the user's offset —
 * four hours, in Asia/Dubai.
 */
function wallClockAsUtcIso(d: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}` +
    `T${p2(d.getHours())}:${p2(d.getMinutes())}:00Z`
  );
}

/**
 * "12 Hours" / "90 Mins" / "1 Hours 30 Mins" — the shape
 * `DoctorPresence.durationDescription` already arrives in, so
 * `presenceShiftLabel` renders a device shift exactly like a doctor's.
 */
function durationDescription(start: Date, end: Date): string {
  const totalMins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${mins} Mins`;
  if (mins === 0) return `${hours} Hours`;
  return `${hours} Hours ${mins} Mins`;
}

/**
 * Project `GET /api/Slots/ResourceAvailability` onto the shift shape the
 * break form reads.
 *
 * Two jobs beyond the field rename:
 *
 *  1. MERGE abutting windows. The feed subtracts each slot separately, so a
 *     device free 08:00-20:00 with nothing booked can still come back as
 *     several touching periods. Offering "8a - 12p" and "12p - 8p" as two
 *     shifts for one uninterrupted working day is noise, and worse, it caps
 *     what a single block can span. Windows that touch (end == next start)
 *     are one shift; a real gap — a booking — stays a real gap.
 *  2. Re-stamp the wall clock as UTC. See `wallClockAsUtcIso`.
 *
 * Exported for its own unit test: both rules are silent when wrong (a shift
 * list that merely looks plausible), so they are pinned directly.
 */
export function deviceShiftsFromAvailability(
  periods: readonly ResourceAvailabilityPeriod[] | null | undefined,
): DoctorPresence[] {
  const windows = parseAvailability(periods);
  const merged: { start: Date; end: Date }[] = [];
  for (const w of windows) {
    const last = merged[merged.length - 1];
    if (last && w.start.getTime() <= last.end.getTime()) {
      // Touching or overlapping — extend rather than start a new shift.
      if (w.end.getTime() > last.end.getTime()) last.end = w.end;
      continue;
    }
    merged.push({ start: new Date(w.start), end: new Date(w.end) });
  }
  return merged.map((w) => {
    // NOT a bug, and NOT removable. `ResourceAvailability` ships clinic wall
    // clock with no zone and `device-availability.ts` parses it TEXTUALLY into
    // a local Date, while the break form reads a shift with `getUTCHours()`
    // because `/api/Doctor/Presence` ships that same wall clock stamped `Z`.
    // Replacing this with a plain local ISO shifts every device shift by the
    // viewer's offset — four hours in Asia/Dubai — and the dropdown still
    // looks entirely plausible. See `wallClockAsUtcIso` and its tests.
    const start = wallClockAsUtcIso(w.start);
    const end = wallClockAsUtcIso(w.end);
    return {
      // The doctor feed's `id` is a presence-row GUID; a window has none, so
      // the range itself is the key. `shiftToKey` in the break form already
      // falls back to `start`, but two windows can share a start only if the
      // merge above failed — keying on both makes that impossible to hide.
      id: `${start}|${end}`,
      start,
      end,
      duration: null,
      durationDescription: durationDescription(w.start, w.end),
      isReadOnly: false,
      isAnytime: false,
      isMoment: false,
      hasStart: true,
      hasEnd: true,
    } satisfies DoctorPresence;
  });
}

export function useActorShifts({
  kind,
  actorId,
  practiceId,
  date,
}: UseActorShiftsParams): UseActorShiftsResult {
  const isDevice = kind === BLOCK_ACTOR_DEVICE;

  const presenceQuery = useDoctorPresence({
    practitionerId: isDevice ? null : actorId,
    date: isDevice ? null : date,
  });

  // Whole selected day, local clock — the same bounds the Devices tab asks
  // for its column shading with, so the panel and the column agree about
  // when the machine is working.
  const deviceParams =
    isDevice && actorId && practiceId && date
      ? {
          deviceId: actorId,
          practiceId,
          from: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
          to: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 0),
        }
      : null;
  const availabilityQuery = useResourceAvailability(deviceParams);

  const deviceShifts = useMemo(
    () => (isDevice ? deviceShiftsFromAvailability(availabilityQuery.data) : []),
    [isDevice, availabilityQuery.data],
  );

  if (isDevice) {
    return {
      shifts: deviceShifts,
      // `useResourceAvailability` is fed a null-shaped params object when
      // disabled, so `isLoading` is only meaningful once there is an actor.
      isLoading: !!deviceParams && availabilityQuery.isLoading,
    };
  }
  return {
    shifts: presenceQuery.data ?? [],
    isLoading: presenceQuery.isLoading,
  };
}
